/**
 * Instructor "grading inbox" for Assignments (design I1).
 *
 * Sections, in order:
 *   grading   — something submitted and not yet graded (oldest submission first)
 *   open      — due in the future, or no due date
 *   scheduled — weekly assignments whose first week hasn't started yet
 *   closed    — due date passed and nothing left to grade (most recent first)
 *
 * Counts come from the assignments list endpoint (instructor/admin only):
 * enrolled_count, submitted_count, submitted_week, to_grade_count,
 * oldest_ungraded_at. Within open/scheduled/closed the incoming order
 * (the page's sort) is kept.
 */
import { toChicago, chicagoToday, chicagoNow, dayDiff, plural, tileFor, fmtTime } from '../../agenda/agendaCore';
import { htmlToText } from './assignmentAgenda';

export const INSTRUCTOR_SECTIONS = [
  { key: 'grading', label: 'Needs grading', tone: 'attention', initialLimit: Infinity },
  { key: 'open', label: 'Open now', tone: 'default', initialLimit: Infinity },
  { key: 'scheduled', label: 'Scheduled', tone: 'default', initialLimit: 5 },
  { key: 'closed', label: 'Closed', tone: 'default', initialLimit: 5 },
];

const isTrue = (v) => v === true || v === 1 || v === '1';
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Weekly assignments arrive as one row per week; keep one row per assignment. */
export function collapseWeeks(assignments, now = chicagoNow()) {
  const byId = new Map();
  assignments.forEach((a) => {
    const list = byId.get(a.id) || [];
    list.push(a);
    byId.set(a.id, list);
  });
  const out = [];
  byId.forEach((rows) => {
    if (rows.length === 1) { out.push(rows[0]); return; }
    // The week that is due next (or the last week, once all have passed).
    const withDue = rows.map((r) => ({ r, due: toChicago(r.display_due_date || r.due_date) }));
    const upcoming = withDue.filter((x) => x.due && x.due >= now).sort((a, b) => a.due - b.due);
    const pick = upcoming[0] || withDue.sort((a, b) => num(b.r.week_number) - num(a.r.week_number))[0];
    out.push(pick.r);
  });
  // keep first-seen order (the page's sort)
  const order = new Map();
  assignments.forEach((a, i) => { if (!order.has(a.id)) order.set(a.id, i); });
  return out.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

function statusFor(due, today, now) {
  if (!due) return { text: 'Open', tone: 'later' };
  const diff = dayDiff(due, today);
  if (due < now) {
    if (diff === 0) return { text: 'Closed today', tone: 'late' };
    if (diff === -1) return { text: 'Closed yesterday', tone: 'late' };
    return { text: `Closed ${plural(-diff, 'day')} ago`, tone: diff >= -7 ? 'late' : 'neutral' };
  }
  if (diff === 0) return { text: 'Due today', tone: 'soon' };
  if (diff === 1) return { text: 'Due tomorrow', tone: 'soon' };
  if (diff <= 7) return { text: `Due in ${plural(diff, 'day')}`, tone: 'later' };
  return { text: `Due ${due.toFormat('LLL d')}`, tone: 'later' };
}

export function buildInstructorAssignmentRow(a, { onGrade, onEdit, onDelete, onViewPdf } = {}, clock = {}) {
  const now = clock.now || chicagoNow();
  const today = clock.today || chicagoToday();
  const weekly = isTrue(a.allow_weekly_submissions);
  const extra = isTrue(a.is_extra_credit);
  const due = toChicago(a.display_due_date || a.due_date);
  const start = weekly ? toChicago(a.start_date) : null;
  const toGrade = num(a.to_grade_count);
  const submitted = num(a.submitted_count);
  const total = num(a.enrolled_count);

  let section;
  if (toGrade > 0) section = 'grading';
  else if (start && start > now) section = 'scheduled';
  else if (!due || due >= now) section = 'open';
  else section = 'closed';

  const status = section === 'scheduled'
    ? { text: `Starts ${start.toFormat('LLL d')}`, tone: 'neutral' }
    : statusFor(due, today, now);

  const typeLabel = weekly
    ? `Weekly${a.week_number ? ` · Week ${a.week_number}` : ''}`
    : extra ? 'Extra Credit' : 'Assignment';

  const title = htmlToText(a.title);
  const cta = toGrade > 0
    ? { label: `Grade ${toGrade}`, icon: 'grade', variant: 'primary', onClick: () => onGrade?.(a) }
    : { label: 'View submissions', icon: 'users', variant: 'secondary', onClick: () => onGrade?.(a) };

  const menu = [
    { key: 'edit', label: 'Edit', onSelect: () => onEdit?.(a) },
    ...(a.pdf_attachment ? [{ key: 'pdf', label: 'View attached PDF', onSelect: () => onViewPdf?.(a) }] : []),
    { key: 'delete', label: 'Delete', danger: true, onSelect: () => onDelete?.(a) },
  ];

  return {
    id: `instr-assignment-${a.id}`,
    anchor: `instr-assignment-${a.id}`,
    section,
    sortKey: section === 'grading' ? (toChicago(a.oldest_ungraded_at)?.toMillis() ?? Infinity) : 0,
    title,
    typeLabel,
    typeTone: weekly ? 'weekly' : extra ? 'extra' : undefined,
    courseCode: a.course_code || '',
    courseName: a.course_name || '',
    tile: tileFor(due),
    tileTone: status.tone,
    status,
    dueLine: due ? `Due ${due.toFormat('LLL d')} · ${fmtTime(due)}` : 'No due date',
    progress: total > 0 || submitted > 0
      ? { submitted, total: Math.max(total, submitted), toGrade, label: weekly && a.submitted_week ? `Week ${a.submitted_week}` : '' }
      : null,
    cta,
    menu,
    assignment: a,
  };
}

export function buildInstructorAssignmentAgenda(assignments, handlers = {}, clock = {}) {
  const now = clock.now || chicagoNow();
  const rows = collapseWeeks(assignments || [], now).map((a) => buildInstructorAssignmentRow(a, handlers, clock));
  const sections = INSTRUCTOR_SECTIONS.map((meta) => {
    let list = rows.filter((r) => r.section === meta.key);
    if (meta.key === 'grading') list = [...list].sort((x, y) => x.sortKey - y.sortKey);
    return { ...meta, rows: list };
  }).filter((s) => s.rows.length > 0);
  const toGradeTotal = rows.reduce((sum, r) => sum + (r.progress?.toGrade || 0), 0);
  return { sections, toGradeTotal };
}
